import { useEffect, useMemo } from "react";
import { Trans } from "react-i18next";

import { ReactComponent as TickSvg } from "@/assets/icons/tick-inherit.svg";
import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent } from "@/constants/amplitude";
import { EventScreenTypes, WebEventProps, WebEventTypes } from "@/constants/event";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { useLocalization } from "@/hooks/useLocalization";
import { usePrefix } from "@/hooks/usePrefix";
import i18n from "@/localization/i18next";
import { WeightLossGoal } from "@/types/answers";
import { QuestionComponentProps } from "@/types/common";
import { TARGET_WEIGHT_STRING_KEY } from "@/utils/AnswersProvider";
import { formatDate } from "@/utils/dateFormatter";
import useAnswers from "@/utils/useAnswers";

import { FactorType } from "./factors";
import { getPredictionDate } from "./utils";
import WeightPredictChart from "./WeightPredictChart";
import WeightPrediction from "./WeightPrediction";
import WeightPredictNote from "./WeightPredictNote";

export const InitialWeightPredictScreen = ({ handleNext, isActive }: QuestionComponentProps) => {
    const { getEventType } = usePrefix();
    const { localizedLanguage: lng } = useLocalization();

    const { answers, setAnswers, getAnswersValue } = useAnswers();

    const currentWeight = getAnswersValue(QUESTIONS_IDS.CURRENT_WEIGHT);
    const targetWeight = getAnswersValue(QUESTIONS_IDS.TARGET_WEIGHT);
    const targetWeightString = answers[TARGET_WEIGHT_STRING_KEY] as string;
    const pace = getAnswersValue(QUESTIONS_IDS.PACE);
    const weightLossGoal = getAnswersValue(QUESTIONS_IDS.WEIGHT_LOSS_GOAL);
    const isLowWeightLoss = weightLossGoal === WeightLossGoal.OneToTen;

    useEffect(() => {
        logAnalyticsEvent(WebEventTypes.VIEW_EVENT, {
            [WebEventProps.SCREEN]: getEventType(EventScreenTypes.AI_WEIGHT_FIRST_PREDICT),
        });
    }, [getEventType]);

    const date = useMemo(
        () =>
            isActive
                ? getPredictionDate(
                      currentWeight,
                      targetWeight,
                      pace,
                      isLowWeightLoss ? FactorType.FirstPredictLowLoss : FactorType.FirstPredict
                  )
                : null,
        [currentWeight, targetWeight, pace, isLowWeightLoss, isActive]
    );

    const formattedDateString = date ? formatDate(date, { lng }) : "";

    useEffect(() => {
        date &&
            setAnswers((ans) => ({
                ...ans,
                [QUESTIONS_IDS.FIRST_WEIGHT_PREDICT_DATE]: date,
            }));
    }, [date, setAnswers]);

    return (
        <div className="zing-max-w grid h-full content-between gap-10 text-center text-white female:text-black">
            <div className="grid gap-10">
                <div className="text-zing-24-32 font-semibold">
                    {i18n.t("last_lose_weight_program")}
                </div>
                <div className="grid gap-4">
                    {date && (
                        <>
                            <WeightPrediction weight={targetWeightString} date={date} />
                            <WeightPredictChart
                                currentWeight={currentWeight}
                                targetWeight={targetWeight}
                                targetWeightString={targetWeightString}
                                targetDate={date}
                                showIntermediateResult={false}
                            />
                        </>
                    )}
                    <WeightPredictNote>
                        <Trans i18nKey="initial_great_news" values={{ date: formattedDateString }}>
                            <span className="font-semibold" />
                        </Trans>
                    </WeightPredictNote>
                    <div className="flex items-center justify-center gap-[14px] text-zing-12-18">
                        <TickSvg className="text-zing-warm-neon-green-500 female:text-zing-warm-green-500" />
                        <div>{i18n.t("good_goal_for_yourself")}</div>
                    </div>
                </div>
            </div>
            <StickyButton onClick={() => handleNext()} />
        </div>
    );
};

export default InitialWeightPredictScreen;
